import { apiGet } from '@/api/client';
import {
  readDashboardCount,
  readDashboardScalar,
  readPropertyStatistics,
} from '@/features/dashboard/lib/coerce-dashboard-value';

export const dashboardApi = {
  // --- Portfolio-level metrics ---
  // Each accepts an optional `scope` (the SSI Filter Bar / scope-as-user selection). When empty,
  // the read falls back to the caller's own JWT scope; non-SSI sessions never widen (server-enforced).

  /** Total identified opportunity sq-ft across the caller's portfolio (scope from JWT or override). */
  async getTotalOpportunitySqft(scope: DashboardWidgetScope = {}): Promise<number | null> {
    const res = await apiGet<unknown>(`/dashboard/total-opportunity-sqft${scopeQs(scope)}`);
    return readDashboardScalar(res);
  },

  /** Active-report counts grouped by report status (pie). V3: `{ items }` → array. */
  async getMeasurementStatus(scope: DashboardWidgetScope = {}): Promise<DashboardNameValue[]> {
    const res = await apiGet<{ items: DashboardNameValue[] }>(`/dashboard/measurement-status${scopeQs(scope)}`);
    return res.items;
  },

  /** Live-building counts grouped by property type (bar). V3: `{ items }` → array. */
  async getMeasurementByPropertyType(scope: DashboardWidgetScope = {}): Promise<DashboardNameValue[]> {
    const res = await apiGet<{ items: DashboardNameValue[] }>(
      `/dashboard/measurement-by-property-type${scopeQs(scope)}`,
    );
    return res.items;
  },

  async getTotalOpportunityPercent(scope: DashboardWidgetScope = {}): Promise<number | null> {
    const res = await apiGet<unknown>(`/dashboard/total-opportunity-percent${scopeQs(scope)}`);
    return readDashboardScalar(res);
  },

  async getTotalOpportunityValue(scope: DashboardWidgetScope = {}): Promise<number | null> {
    const res = await apiGet<unknown>(`/dashboard/total-opportunity-value${scopeQs(scope)}`);
    return readDashboardScalar(res);
  },

  /**
   * Count of live buildings with a current report, optionally filtered by report status.
   * V3 scopes by the caller's JWT (or override); a `0` status means "no filter".
   */
  async getBuildingMeasured(reportStatusId = 0, scope: DashboardWidgetScope = {}): Promise<number | null> {
    const qs = scopeQs(scope, { reportStatusId: reportStatusId > 0 ? reportStatusId : undefined });
    const res = await apiGet<unknown>(`/dashboard/building-measured${qs}`);
    return readDashboardCount(res);
  },

  async getRolloverCapturedSqft(scope: DashboardWidgetScope = {}): Promise<number | null> {
    const res = await apiGet<unknown>(`/dashboard/rollover-captured-sqft${scopeQs(scope)}`);
    return readDashboardScalar(res);
  },

  async getRolloverSqftByYear(scope: DashboardWidgetScope = {}): Promise<DashboardYearSqft[]> {
    const res = await apiGet<{ items: DashboardYearSqft[] }>(`/dashboard/rollover-sqft-by-year${scopeQs(scope)}`);
    return res.items;
  },

  /** Portfolio summary KPIs (also feeds the Total Buildings tile via `totalBuildings`). */
  getStatistics(scope: DashboardWidgetScope = {}): Promise<DashboardSummaryStatistics> {
    return apiGet<DashboardSummaryStatistics>(`/dashboard/summary-statistics${scopeQs(scope)}`);
  },

  getSummaryStatistics(scope: DashboardWidgetScope = {}): Promise<DashboardSummaryStatistics> {
    return apiGet<DashboardSummaryStatistics>(`/dashboard/summary-statistics${scopeQs(scope)}`);
  },

  /** Always-visible overview pill stats (V2 `/overview-summary` parity — US-2.1). */
  getOverviewSummary(scope: DashboardWidgetScope = {}): Promise<DashboardOverviewSummary> {
    return apiGet<DashboardOverviewSummary>(`/dashboard/overview-summary${scopeQs(scope)}`);
  },

  /** Monthly identified-opportunity trend over `[startDate, endDate]` (YYYY-MM-DD). */
  async getIdentifiedOpportunity(
    startDate: string,
    endDate: string,
    scope: DashboardWidgetScope = {},
  ): Promise<DashboardTrendPoint[]> {
    const res = await apiGet<{ items: DashboardTrendPoint[] }>(
      `/dashboard/identified-opportunity${scopeQs(scope, { start: startDate, end: endDate })}`,
    );
    return res.items;
  },

  /** Monthly value-realized trend over `[startDate, endDate]` (YYYY-MM-DD). */
  async getValueRealized(
    startDate: string,
    endDate: string,
    scope: DashboardWidgetScope = {},
  ): Promise<DashboardTrendPoint[]> {
    const res = await apiGet<{ items: DashboardTrendPoint[] }>(
      `/dashboard/value-realized${scopeQs(scope, { start: startDate, end: endDate })}`,
    );
    return res.items;
  },

  // --- New visual widgets (V3: JWT-scoped or override; `{ items }` → array) ---

  /** Live buildings with map coordinates. */
  async getBuildingLocations(scope: DashboardWidgetScope = {}): Promise<DashboardBuildingLocation[]> {
    const res = await apiGet<{ items: DashboardBuildingLocation[] }>(`/dashboard/building-locations${scopeQs(scope)}`);
    return res.items;
  },

  /** All occupied leases with a known expiration date (the heatmap buckets these by month). */
  async getLeaseExpirations(scope: DashboardWidgetScope = {}): Promise<DashboardLeaseRow[]> {
    const res = await apiGet<{ items: DashboardLeaseRow[] }>(`/dashboard/lease-expirations${scopeQs(scope)}`);
    return res.items;
  },

  /** Month cell drill-in for the lease-expiration heatmap (grouped by building). */
  getLeaseExpirationCellDetail(
    year: number,
    month: number,
    scope: DashboardWidgetScope = {},
  ): Promise<DashboardLeaseCellDetail> {
    return apiGet<DashboardLeaseCellDetail>(
      `/dashboard/lease-expirations/cell-detail${scopeQs(scope, { year, month })}`,
    );
  },

  /** Merged activity feed (requests + reports; SSI also sees raw user events). */
  async getActivityFeed(scope: DashboardWidgetScope = {}): Promise<DashboardActivityItem[]> {
    const res = await apiGet<{ items: DashboardActivityItem[] }>(`/dashboard/activity-feed${scopeQs(scope)}`);
    return res.items;
  },

  /** Upcoming lease expirations (next 6 months), optionally filtered by building. */
  async getUpcomingLeaseExpirations(
    buildingId?: number,
    scope: DashboardWidgetScope = {},
  ): Promise<DashboardLeaseRow[]> {
    const res = await apiGet<{ items: DashboardLeaseRow[] }>(
      `/dashboard/upcoming-lease-expirations${scopeQs(scope, { buildingId })}`,
    );
    return res.items;
  },

  /** Recently expired leases (last 6 months), optionally filtered by building. */
  async getRecentlyExpiredLeases(
    buildingId?: number,
    scope: DashboardWidgetScope = {},
  ): Promise<DashboardLeaseRow[]> {
    const res = await apiGet<{ items: DashboardLeaseRow[] }>(
      `/dashboard/recently-expired-leases${scopeQs(scope, { buildingId })}`,
    );
    return res.items;
  },

  /** Opportunity sqft grouped by lease expiry year, optionally filtered by building. */
  async getOpportunityByExpiryYear(
    buildingId?: number,
    scope: DashboardWidgetScope = {},
  ): Promise<DashboardYearOpportunity[]> {
    const res = await apiGet<{ items: DashboardYearOpportunity[] }>(
      `/dashboard/opportunity-by-expiry-year${scopeQs(scope, { buildingId })}`,
    );
    return res.items;
  },

  /** Buildings list for filter dropdowns. V3 scopes by JWT (or override) and returns `{ items }`. */
  async getBuildingsList(scope: DashboardWidgetScope = {}): Promise<DashboardBuildingListEntry[]> {
    const res = await apiGet<{ items: DashboardBuildingListEntry[] }>(`/dashboard/buildings-list${scopeQs(scope)}`);
    return res.items;
  },

  // --- Property-level metrics (V3: JWT-scoped, building access enforced server-side) ---

  async getPropertyBaseSqft(buildingId: number): Promise<number | null> {
    const res = await apiGet<unknown>(`/dashboard/property/${buildingId}/base-sqft`);
    return readDashboardScalar(res);
  },

  async getPropertyOpportunitySqft(buildingId: number): Promise<number | null> {
    const res = await apiGet<unknown>(`/dashboard/property/${buildingId}/opportunity-sqft`);
    return readDashboardScalar(res);
  },

  async getPropertyTotalOpportunityValue(buildingId: number): Promise<number | null> {
    const res = await apiGet<unknown>(`/dashboard/property/${buildingId}/opportunity-value`);
    return readDashboardScalar(res);
  },

  async getPropertyTotalFloors(buildingId: number): Promise<number | null> {
    const res = await apiGet<unknown>(`/dashboard/property/${buildingId}/total-floors`);
    return readDashboardScalar(res);
  },

  async getPropertyRentableSqft(buildingId: number): Promise<number | null> {
    const res = await apiGet<unknown>(`/dashboard/property/${buildingId}/rentable-sqft`);
    return readDashboardScalar(res);
  },

  async getPropertyGrossSqft(buildingId: number): Promise<number | null> {
    const res = await apiGet<unknown>(`/dashboard/property/${buildingId}/gross-sqft`);
    return readDashboardScalar(res);
  },

  async getPropertyRolloverCapturedSqft(buildingId: number): Promise<number | null> {
    const res = await apiGet<unknown>(`/dashboard/property/${buildingId}/rollover-captured`);
    return readDashboardScalar(res);
  },

  async getPropertyRolloverByYear(buildingId: number): Promise<DashboardYearSqft[]> {
    const res = await apiGet<{ items: DashboardYearSqft[] }>(`/dashboard/property/${buildingId}/rollover-by-year`);
    return res.items;
  },

  async getPropertyTotalOpportunityPercent(buildingId: number): Promise<number | null> {
    const res = await apiGet<unknown>(`/dashboard/property/${buildingId}/opportunity-percent`);
    return readDashboardScalar(res);
  },

  async getPropertyStatistics(buildingId: number): Promise<DashboardPropertyStatistics> {
    const res = await apiGet<unknown>(`/dashboard/property/${buildingId}/statistics`);
    return readPropertyStatistics(res);
  },

  // Per-tenant rollover status for one building: captured (leased area) vs remaining (opportunity).
  // The legacy C# `GetPropertyTenantRollOverStatus` returned hardcoded demo data and was not ported;
  // this reads the real V3 endpoint computed from migrated tenant rows.
  async getPropertyTenantRolloverStatus(buildingId: number): Promise<DashboardTenantRollover[]> {
    const res = await apiGet<{ items: DashboardTenantRollover[] }>(
      `/dashboard/property/${buildingId}/tenant-rollover`,
    );
    return res.items;
  },

  // ─── PR #49 — DASH-W1-4 access context (tier + role) ─────────

  getAccessContext(): Promise<DashboardAccessContext> {
    return apiGet<DashboardAccessContext>('/dashboard/access-context');
  },

  // ─── PR #49 — DASH-W1-1 cross-module aggregators ──────────────
  // Optional `scope` arg threads the SSI Filter Bar selection into
  // the widget query. Non-SSI sessions silently get these stripped
  // server-side (see resolveScope in server/routes/dashboard.js).

  getOrdersStatus(scope: DashboardWidgetScope = {}): Promise<{ openTotal: number; byStatus: Record<string, number> }> {
    return apiGet(`/dashboard/orders-status${scopeQs(scope)}`);
  },

  getRecentBilling(scope: DashboardWidgetScope = {}): Promise<{ count: number; totalDollars: number; windowDays: number }> {
    return apiGet(`/dashboard/recent-billing${scopeQs(scope)}`);
  },

  /**
   * Recent Drawing Vault activity, scoped to the caller's Drawing Vault company (F-3). Callers
   * without a vault company receive an empty feed.
   */
  async getRecentDrawings(limit = 5): Promise<{ items: RecentDrawingActivity[] }> {
    const res = await apiGet<{ items: RecentDrawingActivity[] }>(
      `/dashboard/recent-drawings?limit=${limit}`,
    );
    return { items: res.items ?? [] };
  },

  getReportsSummary(
    status: 'final' | 'preliminary',
    scope: DashboardWidgetScope = {},
  ): Promise<{ status: string; count: number; opportunitySqFt: number }> {
    return apiGet(`/dashboard/reports-summary${scopeQs(scope, { status })}`);
  },

  async getUnmeasuredBuildings(scope: DashboardWidgetScope = {}): Promise<number | null> {
    const res = await apiGet<unknown>(`/dashboard/unmeasured-buildings${scopeQs(scope)}`);
    return readDashboardCount(res);
  },

  /** Upcoming Rollover analysis (US-3.5). */
  async getLeaseRollover(
    options: {
      yearRange: 4 | 8;
      periodType: 'Quarter' | 'Year' | 'Month';
      unitType: 'sqft' | 'sqm';
    },
    scope: DashboardWidgetScope = {},
  ): Promise<DashboardLeaseRolloverPeriod[]> {
    const res = await apiGet<{ items: DashboardLeaseRolloverPeriod[] }>(
      `/dashboard/lease-rollover${scopeQs(scope, {
        yearRange: options.yearRange,
        periodType: options.periodType,
        unitType: options.unitType,
      })}`,
    );
    return res.items;
  },

  /** City-grouped measured buildings table (US-3.6). */
  getMeasuredBuildingsTable(
    rowsPerGroup: number,
    expandedCities: string[],
    scope: DashboardWidgetScope = {},
  ): Promise<DashboardBuildingTableResponse<DashboardMeasuredBuildingRow>> {
    return apiGet(
      `/dashboard/measured-buildings-table${scopeQs(scope, {
        rowsPerGroup,
        expandedCities: expandedCities.length ? expandedCities.join(',') : undefined,
      })}`,
    );
  },

  /** City-grouped unmeasured buildings table (US-3.6). */
  getUnmeasuredBuildingsTable(
    rowsPerGroup: number,
    expandedCities: string[],
    scope: DashboardWidgetScope = {},
  ): Promise<DashboardBuildingTableResponse<DashboardUnmeasuredBuildingRow>> {
    return apiGet(
      `/dashboard/unmeasured-buildings-table${scopeQs(scope, {
        rowsPerGroup,
        expandedCities: expandedCities.length ? expandedCities.join(',') : undefined,
      })}`,
    );
  },

  /** Pending approved domain-join requests (SSI staff only; hidden under SSI lens — DEF-269). */
  async getApprovedRequestsCount(
    scope: DashboardWidgetScope = {},
  ): Promise<{ count: number | null }> {
    const res = await apiGet<{ count: number }>(
      `/dashboard/approved-requests-count${scopeQs(scope)}`,
    );
    return { count: readDashboardCount(res) };
  },

  /** Pending invites count scoped to the SSI lens / caller company (DEF-269). */
  async getPendingInvitesCount(
    scope: DashboardWidgetScope = {},
  ): Promise<{ count: number }> {
    const res = await apiGet<{ count: number }>(
      `/dashboard/pending-invites-count${scopeQs(scope)}`,
    );
    return { count: res.count ?? 0 };
  },
};

export interface DashboardAccessContext {
  tier: 'BASE' | 'PRO' | 'PRO-LIMITED' | 'HYBRID' | 'ENTERPRISE';
  role: string;
  dashboardEnabled: boolean;
  /** Admin DV grant — independent of TruSpace CBS tier (ADR-0019). */
  drawingVaultEnabled: boolean;
}

/** The company's resolved dashboard/subscription tier (DEF-244 widget gating). */
export type DashboardTier = DashboardAccessContext['tier'];

/** A labeled count, used by the measurement-status pie and measurement-by-type bar. */
export interface DashboardNameValue {
  name: string;
  value: number;
}

/** Portfolio summary KPIs returned by `/dashboard/summary-statistics`. */
export interface DashboardSummaryStatistics {
  totalBuildings: number;
  totalFloors: number;
  totalRentableSqFt: number;
  totalTenants: number;
  totalOpportunitySqFt: number;
  totalOpportunityValue: number;
}

/** Always-visible overview pill stats returned by `/dashboard/overview-summary` (US-2.1). */
export interface DashboardOverviewSummary {
  opportunityProfilePercent: number | null;
  baseClientRsf: number | null;
  targetRsf: number | null;
  totalBuildings: number;
}

/** Id + name pair for building pickers. */
export interface DashboardBuildingListEntry {
  buildingId: number;
  name: string;
}

/** Rollover sq-ft for a single lease-expiration year. */
export interface DashboardYearSqft {
  year: number;
  sqft: number;
}

/** Per-tenant rollover status: captured (leased) vs remaining (opportunity) area. */
export interface DashboardTenantRollover {
  tenantName: string;
  captured: number;
  remaining: number;
}

/** One period bucket in the Upcoming Rollover widget (US-3.5). */
export interface DashboardLeaseRolloverPeriod {
  periodLabel: string;
  sortKey: number;
  yearNumber: number;
  quarterNumber: number | null;
  monthNumber: number | null;
  yearRangeTag: string;
  periodType: 'Quarter' | 'Year' | 'Month';
  unitType: 'sqft' | 'sqm';
  upcomingValue: number;
  opportunityValue: number;
  totalValue: number;
  tenantCount: number;
  buildingCount: number;
  floorCount: number;
}

/** Measured building row inside a city group (US-3.6). */
export interface DashboardMeasuredBuildingRow {
  buildingId: number;
  city: string;
  location: string;
  baseClientRsf: number;
  targetRsf: number;
  type: string;
  opportunity: number;
  opportunityPercent: number | null;
}

/** Unmeasured building row inside a city group (US-3.6). */
export interface DashboardUnmeasuredBuildingRow {
  buildingId: number;
  city: string;
  location: string;
  baseClientRsf: number;
  type: string;
  scheduledDate: string | null;
}

export interface DashboardBuildingTableTotals {
  baseClientRsf: number;
  targetRsf?: number;
  opportunity?: number;
  opportunityPercent?: number | null;
}

export interface DashboardBuildingTableGroup<T> {
  city: string;
  buildingCount: number;
  totals: DashboardBuildingTableTotals;
  rows: T[];
  hasMore: boolean;
}

export interface DashboardBuildingTableResponse<T> {
  groups: DashboardBuildingTableGroup<T>[];
  totalBuildings: number;
}

/** A single point in a monthly trend series (identified-opportunity, value-realized). */
export interface DashboardTrendPoint {
  date: string;
  value: number;
}

/** A building map pin returned by `/dashboard/building-locations`. */
export interface DashboardBuildingLocation {
  buildingsId: number;
  name: string;
  address: string;
  city: string;
  state: string;
  latitude: number;
  longitude: number;
  totalFloors: number;
  baseRentable: number;
}

/** A tenant lease row (lease-expirations, upcoming, recently-expired), joined to its building. */
export interface DashboardLeaseRow {
  tenantId: number;
  tenantName: string;
  suite: string;
  buildingId: number;
  buildingName: string;
  leaseExpirationDate: string;
  rentableArea: number;
}

/** Lease heatmap cell-detail modal — one building group. */
export interface DashboardLeaseCellDetailBuilding {
  buildingId: number;
  buildingName: string;
  buildingCode: string;
  leaseCount: number;
  totalSqft: number;
  leases: Array<{
    tenantId: number;
    tenantName: string;
    suite: string;
    floor: string;
    rentableArea: number;
    leaseExpirationDate: string;
  }>;
}

/** Lease heatmap cell-detail modal payload. */
export interface DashboardLeaseCellDetail {
  year: number;
  month: number;
  leaseCount: number;
  totalSqft: number;
  buildings: DashboardLeaseCellDetailBuilding[];
}

/** A merged activity-feed item. `id` is a stable-within-response React key. */
export interface DashboardActivityItem {
  id: number;
  type: 'property' | 'report' | 'workorder' | 'user' | 'alert';
  title: string;
  description: string;
  timestamp: string;
  userName?: string | null;
}

/** Opportunity sqft + lease count for one lease-expiration year. */
export interface DashboardYearOpportunity {
  year: number;
  sqft: number;
  leaseCount: number;
}

/** Per-building statistics block from `/dashboard/property/:buildingId/statistics`. */
export interface DashboardPropertyStatistics {
  baseSqft: number;
  rentableSqft: number;
  grossSqft: number;
  totalFloors: number;
  opportunitySqft: number;
  opportunityValue: number;
  opportunityPercent: number;
}

/**
 * The SSI Filter Bar / scope-as-user selection threaded into a scoped dashboard read. All three are
 * optional and only honored for SSI callers — the server pins every non-SSI caller to their own
 * resolved scope, so passing these from a non-SSI session is a harmless no-op (see
 * `server/src/lib/building-scope.ts#resolveScopeWithOverride`).
 */
export interface DashboardWidgetScope {
  companyId?: number | null;
  buildingId?: number | null;
  scopeAsUserId?: number | null;
}

/**
 * Build a query string from the scope override plus any endpoint-specific params (`start`, `status`,
 * …). Falsy/empty values are dropped so the URL — and therefore the TanStack Query cache key derived
 * from it — stays stable when no scope is selected.
 */
function scopeQs(
  scope: DashboardWidgetScope,
  extra: Record<string, string | number | undefined> = {},
): string {
  const params = new URLSearchParams();
  for (const [key, val] of Object.entries(extra)) {
    if (val !== undefined && val !== null && val !== '') params.set(key, String(val));
  }
  if (scope.companyId) params.set('scopeCompanyId', String(scope.companyId));
  if (scope.buildingId) params.set('scopeBuildingId', String(scope.buildingId));
  if (scope.scopeAsUserId) params.set('scopeUserId', String(scope.scopeAsUserId));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export interface RecentDrawingActivity {
  id: number;
  drawingId: number;
  drawingTitle: string | null;
  buildingName: string | null;
  activityType: string;
  createdAt: string;
  userName: string | null;
}
